import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Card,
  Button,
  TextField,
  MenuItem,
  Typography,
  Alert,
  Tooltip,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  PlayArrow as PlayIcon,
  Stop as StopIcon,
  FreeBreakfast as BreakIcon,
  AccessTime as ClockIcon,
  CloudOff as OfflineIcon,
  SwapHoriz as SwitchIcon,
  MyLocation as LocationIcon,
  InfoOutlined as InfoIcon,
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import { useTerms } from '../context/TerminologyContext';
import {
  getClockInStatus,
  clockIn,
  clockOut,
  getPunchOptions,
  punchBreak,
  switchProperty,
  cancelClockIn,
  getTaskSettings,
  getTasks,
} from '../api';
import ClockOutDialog from './dialogs/ClockOutDialog';
import { useConfirmDialog } from './ConfirmDialog';
import { getUserTimezone, formatTimeInTimezone } from '../utils/formatTime';
import { queuePunchExclusive, queueSwitchExclusive, lastQueuedShift, replayQueue, getQueueLength, cachePunchOptions, cachedPunchOptions, refreshPunchTokens, setPunchIdentity, PUNCH_TOKEN_REFRESH_MS } from '../utils/offlineQueue';
import { requestLocation, locationRecoverySteps } from '../utils/geolocation';

// Axios errors without a response = network failure (offline / server unreachable)
const isNetworkError = (err) => !err.response;

// The optimistic clock-in shape the card shows for a punch that only exists in
// the offline queue (same shape performClockIn builds when it queues).
const offlineClockInFrom = (item) => ({
  id: 'offline',
  clock_in_time: item.queued_at,
  company_id: item.payload?.company_id,
  affiliate_id: item.payload?.affiliate_id,
  company_name: item.meta?.label,
  task_id: item.payload?.task_id,
  task_title: item.meta?.task_title,
  offline: true,
});

// Server response shape for list endpoints in this app isn't fully settled
// between a bare array and a { tasks: [...] } wrapper — accept either.
const unwrapTasks = (data) => (Array.isArray(data) ? data : (data?.tasks || []));

// Clock in/out card. Managers and admins get it compact on the action-inbox
// home; employees get it `prominent` as the centre of their Dashboard. One
// implementation, so break punches, property switching and the double-tap
// guard behave the same for every role.

// Only the instance with `listenForTaskActions` set reacts to
// `hourtrax:clock-task` (dispatched by My Tasks). App.js only ever mounts one
// tab's component at a time, so in practice a single instance listens — this
// counter is a belt-and-suspenders guard so that IF two ever mounted at once,
// only the most-recently-mounted one applies the punch, never both.
let clockTaskListenerSeq = 0;

const formatElapsedTime = (seconds) => {
  const h = String(Math.floor(seconds / 3600)).padStart(2, '0');
  const m = String(Math.floor((seconds % 3600) / 60)).padStart(2, '0');
  const s = String(Math.floor(seconds % 60)).padStart(2, '0');
  return `${h}:${m}:${s}`;
};

export default function ClockCard({ prominent = false, onStatusChange, listenForTaskActions = false }) {
  const { user } = useAuth();
  const { t } = useTerms();
  const { showConfirm, ConfirmDialogComponent } = useConfirmDialog();
  // Phones are the employee dashboard's main device: bigger tap targets there.
  const buttonSize = prominent ? 'large' : 'small';
  const employeeId = user?.employeeId;

  // What the employee punches against: a property where their client has them,
  // otherwise the client itself. The owning company is derived server-side.
  const [options, setOptions] = useState([]);
  const [optionKey, setOptionKey] = useState('');
  const [currentClockIn, setCurrentClockIn] = useState(null);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [clockOutBreak, setClockOutBreak] = useState(0);
  const [clockOutNotes, setClockOutNotes] = useState('');
  const [breakNotice, setBreakNotice] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [queuedCount, setQueuedCount] = useState(getQueueLength());
  // Shown while the browser's own permission dialog is up, so the employee
  // knows the app is waiting on them rather than stalled.
  const [awaitingLocation, setAwaitingLocation] = useState(false);
  // Location was refused: the browser will not ask again from script, so the
  // only way forward is per-platform instructions.
  const [locationBlocked, setLocationBlocked] = useState(false);
  const [switchOpen, setSwitchOpen] = useState(false);
  // False until the server has said whether they're on the clock.
  const [statusLoaded, setStatusLoaded] = useState(false);
  const [switchKey, setSwitchKey] = useState('');
  // Optional task linking — only surfaced once the tenant has turned tasks on.
  // Any failure here (403 on an older server, feature off) just hides the
  // picker; it never blocks clock in/out.
  const [tasksEnabled, setTasksEnabled] = useState(false);
  const [openTasks, setOpenTasks] = useState([]);
  const [taskId, setTaskId] = useState('');
  const [switchTaskId, setSwitchTaskId] = useState('');
  // Set while a Clock Out was requested via a task card's "Clock out" action
  // (see the `hourtrax:clock-task` listener below), so the success handler
  // knows to announce it back to My Tasks. Cleared on both success and cancel
  // so an unrelated later Clock Out never fires a stale announcement.
  const pendingTaskClockoutRef = useRef(null);
  // The Card root, so a task-driven action (which can land far from where the
  // employee tapped, on My Tasks) can bring this card's inline error/notice
  // on screen instead of leaving it off the bottom of a phone viewport.
  const cardRef = useRef(null);

  const scrollCardIntoView = () => {
    cardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  };

  // Tells My Tasks a task-driven action didn't go anywhere (busy, on break,
  // no property resolvable, or the clock-in/out call itself failed) so it
  // can stop showing a spinner on the card that triggered it. The message is
  // included too — "Clock out & complete" shows it inside the Mark Complete
  // dialog, since this card's own Alert can be rejected before it's ever
  // rendered visibly (its dialog never opens for a rejected request).
  const reportTaskActionFailed = (id, message) => {
    window.dispatchEvent(new CustomEvent('hourtrax:clock-task-error', { detail: { taskId: id, message } }));
  };

  // One place to ask for a fix, so every punch path prompts the same way and
  // reacts the same way to a refusal.
  const captureLocation = useCallback(async () => {
    setAwaitingLocation(false);
    const { location, reason } = await requestLocation({
      onPrompting: () => setAwaitingLocation(true),
    });
    setAwaitingLocation(false);
    if (reason === 'denied') setLocationBlocked(true);
    return location;
  }, []);

  const loadStatus = useCallback(async () => {
    try {
      const res = await getClockInStatus(employeeId);
      setCurrentClockIn(res.data.clocked_in ? res.data.clock_in : null);
    } catch (err) {
      // Offline (no response): the server can't tell us, but the queue can —
      // without this a reload while offline offers Clock In again and queues a
      // duplicate that replay then rejects. Any other failure degrades silently
      // as before; the My Time Entries tab still works.
      if (isNetworkError(err)) {
        const queued = lastQueuedShift(employeeId);
        if (queued) setCurrentClockIn(offlineClockInFrom(queued));
      }
    } finally {
      setStatusLoaded(true);
    }
  }, [employeeId]);

  // Replay any offline-queued punches, then refresh status from the server
  const syncOfflineQueue = useCallback(async () => {
    if (getQueueLength() === 0) return;
    const result = await replayQueue();
    setQueuedCount(result.stillQueued);
    const parts = [];
    if (result.dropped.length > 0) parts.push(`Some queued punches could not sync: ${result.dropped.join('; ')}`);
    if (result.warnings?.length > 0) parts.push(`Some queued punches synced with a different time: ${result.warnings.join('; ')}`);
    if (parts.length > 0) setError(parts.join(' '));
    if (result.replayed > 0) {
      loadStatus();
    }
  }, [loadStatus]);

  useEffect(() => {
    if (!employeeId) return;
    loadStatus();
    getPunchOptions()
      .then(res => {
        const opts = res.data?.options || [];
        setOptions(opts);
        cachePunchOptions(employeeId, opts);
      })
      .catch(() => {
        // Offline reload: fall back to the list from the last online visit so
        // Where / Switch Property stay usable and punches can still be queued.
        const cached = cachedPunchOptions(employeeId);
        if (cached) setOptions(cached);
      });
    // Replay first so in-flight items keep the tokens they were queued with, then
    // replace the token pool: the pool is replaced on every refresh so a token's
    // issued_at is the last moment the app was online - that is what bounds how far
    // back a queued punch may be dated.
    setPunchIdentity({ tenantId: user?.tenantId, employeeId, userId: user?.id });
    syncOfflineQueue().finally(() => refreshPunchTokens(employeeId));
    const onOnline = () => syncOfflineQueue().finally(() => refreshPunchTokens(employeeId));
    window.addEventListener('online', onOnline);
    const tokenTimer = setInterval(() => {
      if (navigator.onLine !== false) refreshPunchTokens(employeeId);
    }, PUNCH_TOKEN_REFRESH_MS);
    return () => {
      window.removeEventListener('online', onOnline);
      clearInterval(tokenTimer);
    };
  }, [employeeId, user?.id, user?.tenantId, loadStatus, syncOfflineQueue]);

  const loadOpenTasks = useCallback(async () => {
    try {
      const res = await getTasks({ mine: true, status: 'open' });
      setOpenTasks(unwrapTasks(res.data));
    } catch {
      setOpenTasks([]);
    }
  }, []);

  // Tasks are an optional, tenant-toggled feature — a 403 (disabled) or a
  // server that predates it must never break clock in/out, so any failure
  // here just leaves the picker hidden.
  useEffect(() => {
    if (!employeeId) return;
    getTaskSettings()
      .then((res) => {
        const enabled = !!res.data?.tasks_enabled;
        setTasksEnabled(enabled);
        if (enabled) loadOpenTasks();
      })
      .catch(() => setTasksEnabled(false));
  }, [employeeId, loadOpenTasks]);

  // Tick the elapsed timer while clocked in
  useEffect(() => {
    if (!currentClockIn?.clock_in_time) {
      setElapsedTime(0);
      return undefined;
    }
    const started = new Date(currentClockIn.clock_in_time).getTime();
    const tick = () => setElapsedTime(Math.max(0, Math.floor((Date.now() - started) / 1000)));
    tick();
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, [currentClockIn]);

  // Lets a parent react to the shift: the employee Dashboard hides its manual
  // entry form while someone is on the clock, so a typed entry can't overlap it.
  // Reported only once the server has answered — assuming "not clocked in"
  // flashed the form at people who were. Second argument is the full clock-in
  // (including task_id) for callers that need more than the boolean —
  // existing callers pass a plain state setter and simply ignore the extra arg.
  useEffect(() => {
    if (!onStatusChange) return;
    if (!employeeId) onStatusChange(false, null);
    else if (statusLoaded) onStatusChange(Boolean(currentClockIn), currentClockIn);
  }, [currentClockIn, onStatusChange, statusLoaded, employeeId]);

  // My Tasks drives clock in/out from a task card through this event rather
  // than duplicating any of the logic below. The handlers themselves are
  // defined further down (they close over state declared below this point),
  // so the listener always calls through this ref — kept current on every
  // render — rather than depending on them directly, which would mean
  // tearing the listener down and back up every second while the elapsed
  // timer ticks.
  const taskActionHandlersRef = useRef({});
  useEffect(() => {
    if (!listenForTaskActions) return undefined;
    clockTaskListenerSeq += 1;
    const myId = clockTaskListenerSeq;
    const handler = (e) => {
      if (myId !== clockTaskListenerSeq) return; // superseded by a newer instance
      const { taskId, action } = e.detail || {};
      if (!taskId || !action) return;
      const handlers = taskActionHandlersRef.current;
      if (action === 'start') handlers.handleTaskClockIn?.(taskId);
      else if (action === 'switch') handlers.handleTaskSwitchOpen?.(taskId);
      else if (action === 'clockout') handlers.handleTaskClockOutRequest?.(taskId);
    };
    window.addEventListener('hourtrax:clock-task', handler);
    return () => window.removeEventListener('hourtrax:clock-task', handler);
  }, [listenForTaskActions]);

  if (!employeeId) return null;

  // Shared by a manual Clock In and a task-driven "Start" — the only
  // difference between them is how `choice` (where) and `id` (task) get
  // resolved, so the actual clock-in call and its offline fallback live here
  // once. Returns { ok, message } — ok when it landed (server-confirmed or
  // queued offline, both leave the employee clocked in), plus the error text
  // on failure, so a task-driven caller can report both back to My Tasks.
  const performClockIn = async (choice, id) => {
    const task = openTasks.find(o => String(o.id) === String(id));
    setBusy(true);
    setError('');
    const location = await captureLocation();
    const payload = {
      employee_id: employeeId,
      company_id: choice.company_id,
      affiliate_id: choice.affiliate_id || undefined,
      notes: '',
      location,
      task_id: id || undefined,
    };
    try {
      const res = await clockIn(payload);
      setCurrentClockIn(res.data.clock_in);
      setBreakNotice('');
      setTaskId('');
      return { ok: true };
    } catch (err) {
      if (isNetworkError(err)) {
        // Offline: queue the punch and show an optimistic local timer. This
        // mirrors the queued payload's company/affiliate/task so anything
        // reading currentClockIn (task cards' "On the clock" chip, the Clock
        // button's Start/Switch/Clock out choice) is correct immediately,
        // not just after the queue replays.
        const item = await queuePunchExclusive('clock_in', payload, { meta: { label: choice.label, task_title: task?.title } });
        setQueuedCount(getQueueLength());
        setCurrentClockIn(offlineClockInFrom(item));
        setTaskId('');
        return { ok: true };
      }
      const message = err.response?.data?.error || 'Failed to clock in';
      setError(message);
      return { ok: false, message };
    } finally {
      setBusy(false);
    }
  };

  const handleClockIn = async () => {
    const choice = options.find(o => `${o.company_id}-${o.affiliate_id || ''}` === optionKey);
    if (!choice) {
      setError('Select where you are working to clock in');
      return;
    }
    await performClockIn(choice, taskId);
  };

  // Task-driven "Start" (from a task card's Clock button): picks the task —
  // which also fills Where from its property when possible — then clocks in
  // right away. If Where still can't be resolved, this surfaces the same
  // "pick a property" error a manual Clock In would, with the task left
  // selected so the employee only has to choose Where. Already clocked in
  // somewhere (or on another task)? A "Start" must never queue a second
  // clock-in — hand off to the Switch flow instead, same as tapping "Switch
  // to this task" directly would (that path opens its own dialog, so no
  // scroll is needed here for it).
  const handleTaskClockIn = async (id) => {
    if (busy) {
      const message = 'Please wait a moment and try again.';
      setError(message);
      scrollCardIntoView();
      reportTaskActionFailed(id, message);
      return;
    }
    if (currentClockIn) {
      handleTaskSwitchOpen(id);
      return;
    }
    handleTaskPick(id);
    const task = openTasks.find(o => String(o.id) === String(id));
    const resolvedKey = propertyKeyForTask(task) || optionKey;
    const choice = options.find(o => optionKeyOf(o) === resolvedKey);
    if (!choice) {
      const message = 'Select where you are working to clock in';
      setError(message);
      scrollCardIntoView();
      reportTaskActionFailed(id, message);
      return;
    }
    // Clocking in happens right here with no dialog in between, so bring the
    // card into view now — the employee needs to see whatever comes next:
    // success, "awaiting location", or an error.
    scrollCardIntoView();
    const { ok, message } = await performClockIn(choice, id);
    if (!ok) reportTaskActionFailed(id, message);
  };

  const onBreak = Boolean(currentClockIn?.break_started_at);
  const optionKeyOf = (o) => `${o.company_id}-${o.affiliate_id || ''}`;
  // Where they are now, so the switch list never offers the property they are
  // already at (the server refuses that with "already clocked in there") —
  // unless the task picked for the new shift differs from the current one,
  // since the server treats that as a real switch even at the same property.
  const currentKey = currentClockIn?.company_id
    ? optionKeyOf({ company_id: currentClockIn.company_id, affiliate_id: currentClockIn.affiliate_id })
    : '';

  // The punch-options key for a task's own property, or null when the task
  // has no property or that property isn't one of the current punch options.
  const propertyKeyForTask = (task) => {
    if (task && task.company_id) {
      const key = optionKeyOf({ company_id: task.company_id, affiliate_id: task.affiliate_id });
      if (options.some(o => optionKeyOf(o) === key)) return key;
    }
    return null;
  };

  // Shared by both task pickers: picking a task whose property is one of the
  // punch options also fills in that picker's property select — the server
  // would infer the same property from the task if we left it blank, but
  // doing it here keeps what's on screen honest.
  const applyTaskProperty = (task, setPropertyKey) => {
    const key = propertyKeyForTask(task);
    if (key) setPropertyKey(key);
  };

  const handleTaskPick = (id) => {
    setTaskId(id);
    if (!id) return;
    applyTaskProperty(openTasks.find(o => String(o.id) === String(id)), setOptionKey);
  };

  const handleSwitchTaskPick = (id) => {
    setSwitchTaskId(id);
    if (!id) return;
    applyTaskProperty(openTasks.find(o => String(o.id) === String(id)), setSwitchKey);
  };

  // Task-driven "Switch to this task" (from a task card's Clock button while
  // clocked in elsewhere or on no task): opens the same Switch Property
  // dialog a manual Switch does, with the task preselected — the employee
  // still confirms Moving to before anything changes. Mirrors the manual
  // Switch Property button's own `disabled={busy || onBreak}` — nothing here
  // should be reachable while the manual button couldn't be tapped either.
  const handleTaskSwitchOpen = (id) => {
    if (busy) {
      const message = 'Please wait a moment and try again.';
      setError(message);
      scrollCardIntoView();
      reportTaskActionFailed(id, message);
      return;
    }
    if (onBreak) {
      const message = 'End your break first';
      setError(message);
      scrollCardIntoView();
      reportTaskActionFailed(id, message);
      return;
    }
    // Opens the Switch Property dialog — no scroll needed, the dialog itself
    // is what the employee needs to see next.
    setError('');
    setSwitchKey('');
    handleSwitchTaskPick(id);
    setSwitchOpen(true);
  };

  const handleBreak = async (action) => {
    setBusy(true);
    setError('');
    try {
      const location = await captureLocation();
      const res = await punchBreak({ employee_id: employeeId, action, location });
      setCurrentClockIn(prev => (prev ? {
        ...prev,
        break_started_at: res.data.on_break ? new Date().toISOString() : null,
      } : prev));
      if (action === 'end') {
        setBreakNotice(res.data.message || '');
      } else {
        setBreakNotice('');
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to record break');
    } finally {
      setBusy(false);
    }
  };

  const handleSwitchProperty = async () => {
    const choice = options.find(o => `${o.company_id}-${o.affiliate_id || ''}` === switchKey);
    if (!choice) return;
    setBusy(true);
    setError('');
    const location = await captureLocation();
    const task = openTasks.find(o => String(o.id) === String(switchTaskId));
    const timezone = getUserTimezone();
    // Same two bodies /clockin/switch would perform server-side, so an offline
    // switch replays through the existing clock-out and clock-in endpoints.
    const clockOutPayload = { employee_id: employeeId, break_minutes: 0, notes: '', timezone, location };
    const clockInPayload = {
      employee_id: employeeId,
      company_id: choice.company_id,
      affiliate_id: choice.affiliate_id || undefined,
      notes: '',
      location,
      task_id: switchTaskId || undefined,
    };
    const queueTheSwitch = async () => {
      const { items } = await queueSwitchExclusive({
        clockOutPayload,
        clockInPayload,
        meta: { label: choice.label, task_title: task?.title },
      });
      setQueuedCount(getQueueLength());
      setCurrentClockIn(offlineClockInFrom(items[1]));
      setSwitchOpen(false);
      setSwitchKey('');
      setSwitchTaskId('');
      setBreakNotice(`Moved to ${choice.label}. The move is saved on this device and will sync automatically as soon as you're connected.`);
    };
    try {
      if (currentClockIn?.offline) {
        // The open shift exists only in the queue; keep the punches in order
        // behind it, then try to drain the queue in case we are back online.
        await queueTheSwitch();
        syncOfflineQueue();
        return;
      }
      const res = await switchProperty({
        employee_id: employeeId,
        company_id: choice.company_id,
        affiliate_id: choice.affiliate_id || undefined,
        timezone,
        location,
        task_id: switchTaskId || undefined,
      });
      setCurrentClockIn(res.data.clock_in);
      setSwitchOpen(false);
      setSwitchKey('');
      setSwitchTaskId('');
      const hrs = Number(res.data.hours_worked || 0).toFixed(2);
      setBreakNotice(`Moved to ${choice.label}. ${hrs}h saved against the ${t('location.singular', { lower: true })} you left.`);
    } catch (err) {
      if (isNetworkError(err)) {
        await queueTheSwitch();
        return;
      }
      setError(err.response?.data?.error || `Failed to switch ${t('location.plural', { lower: true })}`);
      // The server saved the old shift but could not open the new one. Resync,
      // or the card keeps timing a shift that no longer exists and offers no
      // Clock In to recover with.
      if (err.response?.status === 500 && err.response?.data?.entry) {
        setSwitchOpen(false);
        loadStatus();
      }
    } finally {
      setBusy(false);
    }
  };

  // Clocked in by mistake: drop the open shift without creating a time entry.
  const handleCancelClockIn = async () => {
    const confirmed = await showConfirm({
      title: 'Cancel Clock-In',
      message: 'Cancel this clock-in? No time entry will be created.',
      confirmText: 'Cancel Clock-In',
      confirmColor: 'warning',
      severity: 'warning',
    });
    if (!confirmed) return;
    setBusy(true);
    setError('');
    try {
      await cancelClockIn(employeeId, currentClockIn?.id);
      setCurrentClockIn(null);
      setBreakNotice('');
      setTaskId('');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to cancel clock-in');
    } finally {
      setBusy(false);
    }
  };

  // Opens the normal Clock Out dialog on behalf of a task card's "Clock out"
  // action, remembering the task so a successful clock-out can announce
  // `hourtrax:task-clocked-out` for it (My Tasks' "Clock out & complete"
  // waits on that event before submitting the completion). Mirrors the
  // manual Clock Out button's own `disabled={busy || onBreak}`.
  const handleTaskClockOutRequest = (id) => {
    if (busy) {
      const message = 'Please wait a moment and try again.';
      setError(message);
      scrollCardIntoView();
      reportTaskActionFailed(id, message);
      return;
    }
    if (!currentClockIn) {
      const message = 'You are not clocked in.';
      setError(message);
      scrollCardIntoView();
      reportTaskActionFailed(id, message);
      return;
    }
    if (onBreak) {
      const message = 'End your break first';
      setError(message);
      scrollCardIntoView();
      reportTaskActionFailed(id, message);
      return;
    }
    // Opens the Clock Out dialog — no scroll needed, the dialog itself is
    // what the employee needs to see next.
    pendingTaskClockoutRef.current = id;
    setClockOutBreak(0);
    setClockOutNotes('');
    setError('');
    setDialogOpen(true);
  };

  const handleClockOut = async () => {
    const taskClockoutId = pendingTaskClockoutRef.current;
    setBusy(true);
    setError('');
    const location = await captureLocation();
    const payload = {
      employee_id: employeeId,
      break_minutes: clockOutBreak || 0,
      notes: clockOutNotes || '',
      timezone: getUserTimezone(),
      location,
    };
    const announceTaskClockout = () => {
      if (taskClockoutId) {
        window.dispatchEvent(new CustomEvent('hourtrax:task-clocked-out', { detail: { taskId: taskClockoutId } }));
      }
    };
    try {
      await clockOut(payload);
      setDialogOpen(false);
      setCurrentClockIn(null);
      // A "Moved to…" or "Break ended" note belongs to the shift that just closed.
      setBreakNotice('');
      setTaskId('');
      // Hours just moved onto whatever task this shift was linked to.
      if (tasksEnabled) loadOpenTasks();
      // Terminal — succeeded, so the ref's job (letting My Tasks know which
      // task to announce for) is done.
      pendingTaskClockoutRef.current = null;
      announceTaskClockout();
    } catch (err) {
      if (isNetworkError(err)) {
        await queuePunchExclusive('clock_out', payload);
        setQueuedCount(getQueueLength());
        setDialogOpen(false);
        setCurrentClockIn(null);
        // Terminal — queued for offline replay is treated as a success here,
        // same as everywhere else in this card.
        pendingTaskClockoutRef.current = null;
        announceTaskClockout();
      } else {
        // NOT terminal: the dialog stays open so the employee can retry.
        // Clearing the ref here would mean a successful retry could never
        // announce hourtrax:task-clocked-out, leaving My Tasks' "Clock out &
        // complete" waiting forever — so it's left alone; only a successful
        // clock-out or the dialog actually closing (see onClose below) ends it.
        setError(err.response?.data?.error || 'Failed to clock out');
      }
    } finally {
      setBusy(false);
    }
  };

  // Keep the ref other renders' hooks read from current — see the
  // `hourtrax:clock-task` listener effect declared with the other hooks above.
  taskActionHandlersRef.current = { handleTaskClockIn, handleTaskSwitchOpen, handleTaskClockOutRequest };

  return (
    <Card ref={cardRef} sx={{ px: prominent ? 3 : 2, py: prominent ? 2.5 : 1.5, display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
      <Box sx={{ width: 30, height: 30, borderRadius: '8px', bgcolor: 'primary.soft', color: 'primary.dark', display: 'grid', placeItems: 'center', flex: 'none' }}>
        <ClockIcon sx={{ fontSize: 17 }} />
      </Box>
      <Typography variant="overline" sx={{ color: 'text.faint', letterSpacing: '0.06em', lineHeight: 1.4 }}>
        My time
      </Typography>

      {queuedCount > 0 && (
        <Tooltip title="You punched while offline. These punches are saved on this device with their original times and will sync automatically when your connection returns. Each queued punch carries a sync token picked up while you were online, which is how the server knows its time is genuine.">
          <Chip
            size="small"
            icon={<OfflineIcon sx={{ fontSize: 14 }} />}
            label={`${queuedCount} queued`}
            color="warning"
            variant="outlined"
          />
        </Tooltip>
      )}

      {currentClockIn ? (
        <>
          <Typography sx={{ fontFamily: 'monospace', fontSize: prominent ? '2rem' : '1.05rem', fontWeight: 600 }}>
            {formatElapsedTime(elapsedTime)}
          </Typography>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            {currentClockIn.affiliate_name || currentClockIn.company_name || 'On the clock'}
            {currentClockIn.clock_in_time && ` · since ${formatTimeInTimezone(currentClockIn.clock_in_time, getUserTimezone())}`}
            {currentClockIn.task_title && ` · ${currentClockIn.task_title}`}
          </Typography>
          {onBreak && (
            <Chip size="small" color="warning" label="On break" sx={{ fontWeight: 600 }} />
          )}
          <Box sx={{ flexGrow: 1 }} />
          <Tooltip title={onBreak
            ? 'End your break and go back on the clock. Short rest breaks stay paid; only a meal break of 30 minutes or more is deducted from your hours.'
            : 'Start a break. It is measured from now until you end it, so you do not need to type it at clock-out. Short rest breaks stay paid; only a meal break of 30 minutes or more is deducted.'}>
            <span>
              <Button
                size={buttonSize}
                variant={onBreak ? 'contained' : 'outlined'}
                color="warning"
                startIcon={<BreakIcon />}
                disabled={busy}
                onClick={() => handleBreak(onBreak ? 'end' : 'start')}
                sx={{ mr: 1 }}
              >
                {onBreak ? 'End Break' : 'Break'}
              </Button>
            </span>
          </Tooltip>
          <Button
            size={buttonSize}
            variant="contained"
            color="error"
            startIcon={<StopIcon />}
            disabled={busy || onBreak}
            onClick={() => { setClockOutBreak(0); setClockOutNotes(''); setError(''); setDialogOpen(true); }}
          >
            Clock Out
          </Button>
          {options.length > 1 && (
            <Tooltip title={`Moving to another ${t('location.singular', { lower: true })}? This saves your hours here and starts the clock at the next one — no gap, and each ${t('location.singular', { lower: true })} is billed for its own time.`}>
              <span>
                <Button
                  size={buttonSize}
                  variant="outlined"
                  startIcon={<SwitchIcon />}
                  disabled={busy || onBreak}
                  onClick={() => { setSwitchKey(''); setSwitchTaskId(''); setError(''); setSwitchOpen(true); }}
                >
                  {`Switch ${t('location.singular')}`}
                </Button>
              </span>
            </Tooltip>
          )}
          {!currentClockIn.offline && (
            <Tooltip title="Clocked in by mistake? Removes this clock-in without creating a time entry.">
              <span>
                <Button
                  size={buttonSize}
                  color="inherit"
                  disabled={busy || onBreak}
                  onClick={handleCancelClockIn}
                >
                  Cancel
                </Button>
              </span>
            </Tooltip>
          )}
        </>
      ) : (
        <>
          <TextField
            select
            size="small"
            label="Where"
            value={optionKey}
            onChange={(e) => setOptionKey(e.target.value)}
            sx={{ minWidth: prominent ? 260 : 200, flex: prominent ? '1 1 260px' : undefined }}
            InputLabelProps={{ shrink: true }}
          >
            {options.map(o => (
              <MenuItem key={`${o.company_id}-${o.affiliate_id || ''}`} value={`${o.company_id}-${o.affiliate_id || ''}`}>
                {o.label}{o.group ? ` — ${o.group}` : ''}
              </MenuItem>
            ))}
          </TextField>
          {tasksEnabled && openTasks.length > 0 && (
            // The tooltip lives on an info icon, not around the select: wrapped,
            // it opened on focus and sat over the open menu and the Clock In
            // button, swallowing taps on phones.
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <TextField
                select
                size="small"
                label="Task (optional)"
                value={taskId}
                onChange={(e) => handleTaskPick(e.target.value)}
                sx={{ minWidth: prominent ? 220 : 180 }}
                InputLabelProps={{ shrink: true }}
                SelectProps={{ displayEmpty: true }}
              >
                <MenuItem value="">None</MenuItem>
                {openTasks.map(task => (
                  <MenuItem key={task.id} value={task.id}>{task.title}{task.status === 'pending' ? ' — Pending' : ''}</MenuItem>
                ))}
              </TextField>
              <Tooltip title={`Optionally link this shift to one of your assigned tasks — its hours will count toward the task's logged hours, and picking a task fills in Where if the task has its own ${t('location.singular', { lower: true })}.`}>
                <InfoIcon fontSize="small" color="action" />
              </Tooltip>
            </Box>
          )}
          <Box sx={{ flexGrow: 1 }} />
          <Tooltip title="Start the live clock — a time entry is created when you clock out">
            <span>
              <Button
                size={buttonSize}
                variant="contained"
                startIcon={<PlayIcon />}
                disabled={busy || !optionKey}
                onClick={handleClockIn}
              >
                Clock In
              </Button>
            </span>
          </Tooltip>
        </>
      )}

      {error && <Alert severity="error" onClose={() => setError('')} sx={{ py: 0, width: '100%' }}>{error}</Alert>}
      {breakNotice && (
        <Alert severity="info" onClose={() => setBreakNotice('')} sx={{ py: 0, width: '100%' }}>
          {breakNotice}
        </Alert>
      )}

      {awaitingLocation && (
        <Alert severity="info" icon={<LocationIcon fontSize="small" />} sx={{ py: 0, width: '100%' }}>
          Waiting for you to allow location — check for your browser's prompt.
        </Alert>
      )}

      <Dialog open={switchOpen} onClose={() => setSwitchOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{`Switch ${t('location.singular', { lower: true })}`}</DialogTitle>
        <DialogContent>
          {/* The card's own Alert sits under this dialog's backdrop, so without
              this a refused switch looked like the button did nothing. */}
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Your hours at {currentClockIn?.affiliate_name || currentClockIn?.company_name || `the current ${t('location.singular', { lower: true })}`} are saved up to now,
            and the clock starts again at the {t('location.singular', { lower: true })} you pick. You stay on the clock throughout.
          </Typography>
          <TextField
            select
            fullWidth
            size="small"
            label="Moving to"
            value={switchKey}
            onChange={(e) => setSwitchKey(e.target.value)}
            InputLabelProps={{ shrink: true }}
          >
            {options
              // The current property is hidden UNLESS the task picked below
              // differs from the one on the current shift — the server still
              // treats that as a real switch even at the same property.
              .filter(o => optionKeyOf(o) !== currentKey
                || (switchTaskId && String(switchTaskId) !== String(currentClockIn?.task_id || '')))
              .map(o => (
                <MenuItem key={`${o.company_id}-${o.affiliate_id || ''}`} value={`${o.company_id}-${o.affiliate_id || ''}`}>
                  {o.label}{o.group ? ` — ${o.group}` : ''}
                </MenuItem>
              ))}
          </TextField>
          {tasksEnabled && openTasks.length > 0 && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 2 }}>
              <TextField
                select
                fullWidth
                size="small"
                label="Task (optional)"
                value={switchTaskId}
                onChange={(e) => handleSwitchTaskPick(e.target.value)}
                InputLabelProps={{ shrink: true }}
                SelectProps={{ displayEmpty: true }}
              >
                <MenuItem value="">None</MenuItem>
                {openTasks.map(task => (
                  <MenuItem key={task.id} value={task.id}>{task.title}{task.status === 'pending' ? ' — Pending' : ''}</MenuItem>
                ))}
              </TextField>
              <Tooltip title={`Optionally link the new shift to one of your assigned tasks — you can even pick a different task at the ${t('location.singular', { lower: true })} you're already at.`}>
                <InfoIcon fontSize="small" color="action" />
              </Tooltip>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSwitchOpen(false)}>Cancel</Button>
          <Button variant="contained" disabled={busy || !switchKey} onClick={handleSwitchProperty}>
            Switch
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={locationBlocked} onClose={() => setLocationBlocked(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Location is turned off for this site</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            Location is blocked for HourTrax and it will not be asked for again until you change
            it. It takes a few taps:
          </Typography>
          {locationRecoverySteps().map((step, i) => (
            <Typography key={i} variant="body2" sx={{ mb: 0.75 }}>
              {i + 1}. {step}
            </Typography>
          ))}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLocationBlocked(false)}>Close</Button>
          <Button variant="contained" onClick={() => window.location.reload()}>Reload page</Button>
        </DialogActions>
      </Dialog>

      <ClockOutDialog
        open={dialogOpen}
        onClose={() => {
          // Ignore Cancel/backdrop/Escape while the clock-out request is
          // actually in flight (busy): the request can still succeed after
          // this fires, and handleClockOut would then announce a REAL
          // success for the same taskId right after this fired `cancelled:
          // true` — two contradictory events for one request, and My Tasks'
          // one-shot listener would already be gone by the time the real one
          // arrived. Once busy clears (success, queued-offline, or a
          // retriable failure that leaves the dialog open) Cancel works again.
          if (busy) return;
          // Cancelling a task-triggered Clock Out: tell whoever asked for it
          // (My Tasks' "Clock out & complete") so it stops waiting rather
          // than sitting with its Cancel button never returning control —
          // hourtrax:task-clocked-out is reused here with `cancelled: true`
          // rather than a second event, since My Tasks already listens on it.
          const cancelledTaskId = pendingTaskClockoutRef.current;
          pendingTaskClockoutRef.current = null;
          setDialogOpen(false);
          if (cancelledTaskId) {
            window.dispatchEvent(new CustomEvent('hourtrax:task-clocked-out', { detail: { taskId: cancelledTaskId, cancelled: true } }));
          }
        }}
        onClockOut={handleClockOut}
        currentClockIn={currentClockIn}
        elapsedTime={elapsedTime}
        formatElapsedTime={formatElapsedTime}
        formatTimeInTimezone={formatTimeInTimezone}
        getUserTimezone={getUserTimezone}
        clockOutBreak={clockOutBreak}
        setClockOutBreak={setClockOutBreak}
        clockOutNotes={clockOutNotes}
        setClockOutNotes={setClockOutNotes}
        error={error}
        busy={busy}
      />
      <ConfirmDialogComponent />
    </Card>
  );
}
